import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { PartnerTermsStore } from '../stores/partner-terms.store';
import { AppRoutes } from '../constants/routes';

/**
 * Holds the management app shut until the venue has accepted the current Partner Terms.
 *
 * Everyone at the venue, not just the owner. The terms are what the venue is permitted to operate
 * this software under; until they are agreed there is no agreement to work under, and a manager
 * taking bookings on unapproved terms is the platform running a venue's commercial relationship on
 * nothing. It also removes the way around the question: an owner who declines cannot simply sign
 * in as a manager and carry on.
 *
 * The cost is real and deliberate — an outstanding signature stops the front desk, not only the
 * person who can give it. That is why the block screen tells staff exactly who has to act rather
 * than showing them a button that would 403.
 *
 * Sits on the dashboard shell rather than on each page, so there is one gate rather than a dozen
 * that can drift apart, and the acceptance page itself stays reachable outside it.
 *
 * Never blocks on an unknown state: both flags read false while the terms are still loading and
 * false again if the request failed, matching the other guards in this folder, which all refuse to
 * turn an outage into a lockout.
 */
export const partnerTermsGuard: CanActivateFn = async () => {
  const partnerTerms = inject(PartnerTermsStore);
  const router = inject(Router);

  await partnerTerms.load();

  // Both, not just one: declining after having accepted is reachable, and leaves a venue that is
  // refusing the terms while its acceptance still stands.
  return partnerTerms.needsAcceptance() || partnerTerms.declined()
    ? router.createUrlTree([AppRoutes.PartnerTerms])
    : true;
};
